/* ============================================================================
 * MediaPlayer — a real player, not a mock
 * ----------------------------------------------------------------------------
 * There was no media player in this repo before this file. It plays what the
 * operator gives it: a stream URL, a local file, or a blob picked from disk.
 *
 * HONEST STATUS — what this does NOT do:
 *   • There is no stream provider wired (no Livepeer, no Mux, no HLS ingest).
 *     Nothing is broadcast from here; a "channel" is a URL the operator
 *     supplies or nothing at all.
 *   • Playback depends on the browser's own codec support. An unsupported
 *     format surfaces the element's error, which is shown, not swallowed.
 *   • No autoplay. Browsers block it and it is hostile besides.
 *
 * The empty state is a labelled frame rather than a fake poster, matching the
 * repo's no-feed discipline: zero and unknown are different answers.
 * ========================================================================= */

import { useCallback, useEffect, useRef, useState } from 'react';

export type PlayerStatus =
  | 'empty'
  | 'loading'
  | 'ready'
  | 'playing'
  | 'paused'
  | 'ended'
  | 'error';

export interface MediaPlayerHandle {
  play: () => void;
  pause: () => void;
}

interface Props {
  /** A URL to play. Changing it reloads the element. */
  src?: string;
  /** Shown above the frame. */
  label?: string;
  /** Fill the frame instead of the 16:9 default. */
  fill?: boolean;
  onStatus?: (s: PlayerStatus) => void;
}

function fmt(t: number): string {
  if (!Number.isFinite(t) || t < 0) return '--:--';
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function MediaPlayer({ src, label, fill = false, onStatus }: Props) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [status, setStatus] = useState<PlayerStatus>(src ? 'loading' : 'empty');
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = useCallback(
    (s: PlayerStatus) => {
      setStatus(s);
      onStatus?.(s);
    },
    [onStatus],
  );

  // A new source resets every derived value — stale time/duration would lie.
  useEffect(() => {
    setTime(0);
    setDuration(0);
    setError(null);
    set(src ? 'loading' : 'empty');
  }, [src]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.volume = volume;
    el.muted = muted;
  }, [volume, muted]);

  if (!src) {
    return (
      <div className="stack">
        <div
          style={{
            aspectRatio: fill ? undefined : '16 / 9',
            height: fill ? '100%' : undefined,
            display: 'grid',
            placeItems: 'center',
            background: 'var(--ui-well, var(--ui-surface-2))',
            border: '1px solid var(--ui-border)',
            borderRadius: 'var(--ui-radius-ctl)',
            color: 'var(--ui-muted-text)',
            gap: 6,
            textAlign: 'center',
            padding: 16,
          }}
        >
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              {label ?? 'no source'}
            </div>
            <div className="note">
              Nothing is loaded. Paste a stream URL or choose a local file.
            </div>
            <div className="note note--warn" style={{ marginTop: 8 }}>
              No stream provider is wired — this desk does not broadcast.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 6 }}>
      <div
        style={{
          position: 'relative',
          background: '#000',
          border: '1px solid var(--ui-border)',
          borderRadius: 'var(--ui-radius-ctl)',
          overflow: 'hidden',
        }}
      >
        <video
          ref={ref}
          src={src}
          playsInline
          preload="metadata"
          style={{ display: 'block', width: '100%', aspectRatio: fill ? undefined : '16 / 9' }}
          onLoadedMetadata={(e) => {
            setDuration(e.currentTarget.duration);
            set('ready');
          }}
          onPlay={() => set('playing')}
          onPause={() => set('paused')}
          onEnded={() => set('ended')}
          onWaiting={() => set('loading')}
          onError={() => {
            const code = ref.current?.error?.code;
            setError(
              code === 4
                ? 'Source not supported, or unreachable. Playback depends on this browser’s codecs.'
                : `Playback error (code ${code ?? '?'}).`,
            );
            set('error');
          }}
          onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        />
        {error && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              padding: 14,
              background: 'rgba(0,0,0,.72)',
              color: 'var(--ui-err)',
              fontSize: 11,
              textAlign: 'center',
            }}
          >
            {error}
          </div>
        )}
      </div>

      {/* transport */}
      <div className="hstack" style={{ gap: 6 }}>
        <button
          type="button"
          className="btn"
          onClick={() => {
            const el = ref.current;
            if (!el) return;
            if (el.paused) void el.play().catch(() => set('error'));
            else el.pause();
          }}
        >
          {status === 'playing' ? 'Pause' : 'Play'}
        </button>
        <span className="note" style={{ minWidth: 88, fontVariantNumeric: 'tabular-nums' }}>
          {fmt(time)} / {fmt(duration)}
        </span>
        <input
          type="range"
          min={0}
          max={Math.max(duration, 0.1)}
          step={0.1}
          value={Math.min(time, duration || 0)}
          onChange={(e) => {
            const el = ref.current;
            if (!el) return;
            const t = Number(e.target.value);
            el.currentTime = t;
            setTime(t);
          }}
          aria-label="Seek"
          style={{ flex: '1 1 auto', minWidth: 80 }}
        />
      </div>

      <div className="hstack" style={{ gap: 6 }}>
        <button
          type="button"
          className="btn"
          onClick={() => setMuted((m) => !m)}
          title={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? 'Unmute' : 'Mute'}
        </button>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={volume}
          onChange={(e) => setVolume(Number(e.target.value))}
          aria-label="Volume"
          style={{ width: 110 }}
        />
        <button
          type="button"
          className="btn"
          onClick={() => void ref.current?.requestFullscreen?.().catch(() => undefined)}
        >
          Fullscreen
        </button>
        <span className="tag tag--muted">{status}</span>
      </div>
    </div>
  );
}
